import React, {useContext, useEffect, useState} from "react";
import _ from "lodash";
import ShowProgressSpinner from "../../../common/ui/show-progress-spinner";
import {DocumentTextIcon, EyeIcon, EyeSlashIcon, PlusIcon, XMarkIcon} from "@heroicons/react/24/solid";
import SelectColorTypeDetails from "../../../design/components/design-editor/settings/select-color-type-details";
import ProductImageSmall from "../../../pod/components/product-upload/product-image-small";
import DeleteItem from "../../../common/ui/delete-item";
import RenderFields from "../../../common/ui/render-fields";
import appContext from "../../../common/context/app-context";
import {resetImagePosition} from "../../../shopify/utils/featured-image-util";
import RenderMediaStage from "./render-media-stage";
import {getAllDimensions} from "../../../master-data/services/dimension-data.service";
import {handleFieldValueChanges, prepareInputFields} from "../../../common/utils/field-change-util";
import {addSingleMedia, updateSingleMedia} from "../../services/media-data.service";
import {
    DEFAULT_COLOR_SETUP, DEFAULT_MEDIA_IMAGE_SETTINGS,
    DEFAULT_TEXT_SETTINGS, IMAGE_FIELD_LIST,
    KEY_PREFIX,
    MEDIA_IMAGE_INPUT_FIELDS
} from "../../constants/media.constant";
import {getNextItemKey} from "../../../common/utils/item-key-util";
import {
    FLIP_DIRECTION_KEY
} from "../../../design/constants/design.constant";
import {SwatchIcon} from "@heroicons/react/20/solid";
import EditFontDetails from "../../../design/components/design-editor/settings/edit-font-details";
import EditStrokeDetails from "../../../design/components/design-editor/settings/edit-stroke-details";
import EditShadowDetails from "../../../design/components/design-editor/settings/edit-shadow-details";
import EditRotationDetails from "../../../design/components/design-editor/settings/edit-rotation-details";
import EditTextPathDetails from "../../../design/components/design-editor/settings/edit-text-path-details";
import {processColorDetails} from "../../../common/utils/color-util";
import {processTextPathData} from "../../../design/utils/svg-data.util";
import EditFlipDetails from "../../../design/components/design-editor/settings/edit-flip-details";
import {generateLinearGradientDetail} from "../../../common/utils/tailwind-color-util";
import {processBackgroundDetails} from "../../../design/utils/background-util";
import {getAllDesignInfo, getSingleDesignInfo} from "../../../shopify/services/design-info-data.service";
import {DESIGN_INFO_IDS} from "../../../shopify/constants/design-info.constant";
import SelectDesignProductImages from "../../../shopify/components/design-info/select-design-product-images";
import UpdateShopifyCollectionImage from "../../../shopify/components/collections/update-shopify-collection-image";
import {collection} from "firebase/firestore";

const defaultSettings = DEFAULT_MEDIA_IMAGE_SETTINGS;

const AddEditImageMedia = ({
                               selectedImageMedia = undefined,
                               refreshImageMediaList = undefined,
                               afterSaveAction = undefined,
                               isAddImageMedia = false,
                               isEditImageMedia = false,
                               mediaFor = undefined,
                               designInfo = undefined,
                               isBlogPost = false,
                               isCollection = false,
                               cssClass = 'button-green px-2 py-1'
                           }) => {
    const [showModal, setShowModal] = useState(false);
    const [imageMedia, setImageMedia] = useState(null);
    const [saveInProgress, setSaveInProgress] = useState(false);
    const [settings, setSettings] = useState(defaultSettings);
    const [imageFields, setImageFields] = useState([]);
    const [layers, setLayers] = useState([]);
    const [dimensionsMap, setDimensionsMap] = useState(null);
    const [dimensionsList, setDimensionsList] = useState([]);
    const [defaultDimension, setDefaultDimension] = useState(null);
    const [selectedLayer = {}, setSelectedLayer] = useState(null);
    const [showImages, setShowImages] = useState(false);
    const [showBackgroundColorSettings, setShowBackgroundColorSettings] = useState(false);
    const [showTextSettings, setShowTextSettings] = useState(false);
    const [selectedImage, setSelectedImage] = useState(null);
    const [showDesigner, setShowDesigner] = useState(false);
    const [allDesignInfo, setAllDesignInfo] = useState(null);
    const [allDesigns, setAllDesigns] = useState(null);
    const [imageBase64, setImageBase64] = React.useState(null);
    const [currentStageRef, setCurrentStageRef] = React.useState(null);
    const [currentStageScale, setCurrentStageScale] = React.useState(null);

    useEffect(() => {
        if (showModal) {
            init();
        }

    }, [showModal]);

    const init = async () => {
        const newDefaultDimension = await prepareMediaImageFields();
        await getAllDesignsDesignInfo();

        if (isEditImageMedia) {
            setImageMedia(selectedImageMedia);
            setSettings(selectedImageMedia?.settings || defaultSettings);
            setLayers(selectedImageMedia?.layers || []);
            prepareStageContainerSize(selectedImageMedia?.settings || defaultSettings);
        } else {

            if (mediaFor) {
                initMediaFor(newDefaultDimension);
            } else {
                setImageMedia({});
                prepareStageContainerSize(defaultSettings);
            }
        }
    }

    const initMediaFor = (newDefaultDimension) => {
        const newMedia: any = {};
        newMedia.name = mediaFor?.name || mediaFor?.title;
        newMedia.mediaFor = {
            id: mediaFor?.id,
            name: mediaFor?.name || mediaFor?.title,
            nodeType: mediaFor?.nodeType || isBlogPost ? 'Blog Post' : 'Design',
            isBlogPost: isBlogPost,
        };

        if (newDefaultDimension) {
            newMedia.dimensionName = newDefaultDimension.name;
            newMedia.dimensionType = newDefaultDimension.id;

            newMedia.size = newDefaultDimension?.width + ' x ' + newDefaultDimension?.height + ' px';

            const existingSettings = {...settings};
            existingSettings.width = _.parseInt(newDefaultDimension?.width);
            existingSettings.height = _.parseInt(newDefaultDimension?.height);

            newMedia.settings = existingSettings;
            setSettings(existingSettings);
        }

        setImageMedia(newMedia);
        prepareStageContainerSize(newMedia?.settings || defaultSettings);

        initDefaultLayers();
    }

    const handleConfirmAction = async () => {
        await saveImageMediaDetails();

        if (refreshImageMediaList) {
            refreshImageMediaList();
        }

        setShowModal(false);
    }

    const getAllDesignsDesignInfo = async () => {
        if (designInfo) {
            await getAllDesignsDesignInfoForFilters();
        } else {
            const allDesignInfoList = await getAllDesignInfo();
            const allDesignsDesignInfo = _.find(allDesignInfoList, {id: DESIGN_INFO_IDS.ALL_DESIGNS});
            const tempDesignInfoList = _.filter(allDesignInfoList, (designInfo) => designInfo?.id !== DESIGN_INFO_IDS.ALL_DESIGNS);

            setAllDesignInfo(allDesignsDesignInfo);

            const allDesignsResult = _.orderBy(tempDesignInfoList, ['productType', 'category', 'name'], ['asc', 'asc', 'asc']);
            setAllDesigns(allDesignsResult);
        }

    }

    const getAllDesignsDesignInfoForFilters = async () => {
        const designInfoResult = await getSingleDesignInfo(DESIGN_INFO_IDS.ALL_DESIGNS);

        setAllDesignInfo(designInfoResult);
        setAllDesigns([designInfo]);
    }

    const saveImageMediaDetails = async () => {
        setSaveInProgress(true);

        const existingImageMedia: any = {...imageMedia};
        existingImageMedia.layers = [...layers];
        existingImageMedia.settings = {...settings};

        if (isBlogPost && mediaFor?.id) {
            existingImageMedia.blogPostId = mediaFor?.id;

            if (designInfo) {
                existingImageMedia.designId = designInfo?.id;
            }
        }

        if (isAddImageMedia && !existingImageMedia?.id) {
            const addedImageMediaDoc = await addSingleMedia(existingImageMedia);
            existingImageMedia.id = addedImageMediaDoc?.id;
            setImageMedia(existingImageMedia);
        } else {
            await updateSingleMedia(existingImageMedia);
        }

        if (afterSaveAction) {
            afterSaveAction(existingImageMedia);
        }

        setSaveInProgress(false);
    }


    const handleCloseAction = () => {
        setShowModal(false);
    }

    const prepareMediaImageFields = async () => {
        const inputFieldsMap = _.keyBy(MEDIA_IMAGE_INPUT_FIELDS, 'attribute');
        let existingDimensions = [...dimensionsList];

        if (_.isEmpty(existingDimensions)) {
            existingDimensions = await getAllDimensions();
            setDimensionsList(existingDimensions);
        }

        inputFieldsMap['dimensionType'].options = existingDimensions?.map((dimension) => {
            return {label: dimension?.name, key: dimension?.id};
        });

        setImageFields(_.values(inputFieldsMap));
        setDimensionsMap(_.keyBy(existingDimensions, 'id'));

        let newDefaultDimension = existingDimensions?.find((dimension) => dimension?.isDefaultdimension);

        if (isBlogPost) {
            const blogPostDimension = existingDimensions?.find((dimension) => dimension?.isBlogPost);
            if (blogPostDimension) {
                newDefaultDimension = blogPostDimension;
            }
        }

        setDefaultDimension(newDefaultDimension);

        return newDefaultDimension;
    }

    const handleSelectedProductImageInLayer = (productImage) => {
        const currentLayer = {...selectedLayer};
        const existingSelectedImages = _.filter(currentLayer?.children, (childItem) => childItem?.isImage) || [];

        productImage.layerKey = currentLayer.key;
        productImage.isImage = true;

        const existingProductImage = existingSelectedImages?.find((selectedProductImage) => selectedProductImage?.id === productImage?.id);
        if (!existingProductImage) {
            resetImagePosition(productImage, settings);

            if (!currentLayer?.children) {
                currentLayer.children = [];
            }

            productImage.displayOrder = currentLayer?.children?.length + 1;

            currentLayer.children.push(productImage);
        } else {
            currentLayer.children = currentLayer?.children?.filter((childItem) => childItem?.id !== productImage?.id);
        }

        currentLayer.children = _.sortBy(currentLayer?.children, ['displayOrder']);

        setSelectedLayer(currentLayer);
        const existingLayers = [...layers];
        const currentLayerIndex = _.findIndex(existingLayers, (layer) => layer?.key === currentLayer?.key);
        existingLayers[currentLayerIndex] = currentLayer;
        setLayers(existingLayers);
    }

    const handleImageFieldChanges = (event) => {
        const existingImageMedia: any = {...imageMedia};

        if (event?.target?.name === 'dimensionType') {
            const selectedDimension = dimensionsMap[event?.target?.value];
            existingImageMedia.size = selectedDimension?.width + ' x ' + selectedDimension?.height + ' px';
            existingImageMedia.dimensionName = selectedDimension?.name;

            const existingSettings = {...settings};
            existingSettings.width = _.parseInt(selectedDimension?.width);
            existingSettings.height = _.parseInt(selectedDimension?.height);

            prepareStageContainerSize(existingSettings, true);
        }

        handleFieldValueChanges(event, existingImageMedia, setImageMedia);
    }

    const initDefaultLayers = () => {
        const newLayers = [];

        newLayers.push({
            name: 'Layer 1',
            key: getNextItemKey(newLayers, KEY_PREFIX.LAYER),
            displayOrder: 1,
        });

        newLayers.push({
            name: 'Layer 2',
            key: getNextItemKey(newLayers, KEY_PREFIX.LAYER),
            displayOrder: 2,
        });

        newLayers.push({
            name: 'Layer 3',
            key: getNextItemKey(newLayers, KEY_PREFIX.LAYER),
            displayOrder: 3,
        });

        showOrHideBackgroundColorSettings(newLayers[0]);
        // addNewText(newLayers[2]);

        setLayers(newLayers);
    }

    const addNewLayer = () => {
        const existingLayers = [...layers];

        existingLayers.push({
            name: 'Layer ' + (existingLayers?.length + 1),
            key: getNextItemKey(existingLayers, KEY_PREFIX.LAYER),
            displayOrder: existingLayers?.length + 1,
        });

        setLayers(existingLayers);
    }

    const addNewText = (layer) => {
        const currentLayer = {...selectedLayer};

        if (currentLayer?.key !== layer?.key) {
            setShowTextSettings(true);
        } else {
            setShowTextSettings(!showTextSettings);
        }

        const newText = {
            isText: true,
            isEditable: true,
            layerKey: layer?.key,
            key: getNextItemKey(currentLayer?.children, KEY_PREFIX.TEXT),
            displayOrder: 0,
            text: 'New Text',
            ...DEFAULT_TEXT_SETTINGS
        }

        if (!layer?.children) {
            layer.children = [];
        }

        layer.children.push(newText);
        layer.children = _.sortBy(layer?.children, ['displayOrder']);

        const existingLayers = [...layers];
        const currentLayerIndex = _.findIndex(existingLayers, (layer) => layer?.key === currentLayer?.key);
        existingLayers[currentLayerIndex] = currentLayer;
        setLayers(existingLayers);

        setSelectedLayer(layer);
    }

    const showOrHideBackgroundColorSettings = (layer) => {
        const currentLayer = {...selectedLayer};

        if (currentLayer?.key !== layer?.key) {
            setShowBackgroundColorSettings(true);
        } else {
            setShowBackgroundColorSettings(!showBackgroundColorSettings);
        }

        const newBackground = {
            isBackground: true,
            layerKey: layer?.key,
            key: getNextItemKey(currentLayer?.children, KEY_PREFIX.BACKGROUND),
            displayOrder: 0,
            settings: {
                height: settings?.height,
                width: settings?.width,
                background: DEFAULT_COLOR_SETUP,
            }
        }

        if (!layer?.children) {
            layer.children = [];

            const linearGradientDetail = generateLinearGradientDetail();
            newBackground.settings.background = {
                ...newBackground.settings.background,
                ...linearGradientDetail
            };

            processBackgroundDetails(newBackground?.settings?.background, newBackground.settings);
        }

        layer.children.push(newBackground);
        layer.children = _.sortBy(layer?.children, ['displayOrder']);

        const existingLayers = [...layers];
        const currentLayerIndex = _.findIndex(existingLayers, (layer) => layer?.key === currentLayer?.key);
        existingLayers[currentLayerIndex] = currentLayer;
        setLayers(existingLayers);

        setSelectedLayer(layer);
    }


    const addNewImage = (layer) => {
        const currentLayer = {...selectedLayer};

        if (currentLayer?.key !== layer?.key) {
            setShowImages(true);
        } else {
            setShowImages(!showImages);
        }

        setSelectedLayer(layer);
    }

    const removeLayer = (layer) => {
        const existingLayers = [...layers];
        const filteredLayers = existingLayers?.filter((existingLayer) => existingLayer?.key !== layer?.key);
        setLayers(filteredLayers);
    }

    const removeChild = (layerIndex, childItemIndex) => {
        const existingLayers = [...layers];
        const existingLayer = existingLayers[layerIndex];
        existingLayer.children = existingLayer?.children?.filter((childItem, index) => index !== childItemIndex);
        existingLayers[layerIndex] = existingLayer;
        setLayers(existingLayers);
    }

    const toggleChildEditable = (layerIndex, childIndex) => {
        const existingLayers = [...layers];
        const existingLayer = existingLayers[layerIndex];
        const existingChildItem = existingLayer?.children[childIndex];
        existingChildItem.isEditable = !existingChildItem?.isEditable;
        existingLayer.children[childIndex] = existingChildItem;
        existingLayers[layerIndex] = existingLayer;
        setLayers(existingLayers);
    }

    const handleSettingsChanges = (event, layerIndex, childIndex) => {
        const existingLayers = [...layers];
        const existingLayer = existingLayers[layerIndex];
        const existingChild = existingLayer?.children[childIndex];

        let existingSettings = existingChild?.settings || {};

        existingSettings = handleFieldValueChanges(event, existingSettings);
        existingChild.settings = existingSettings;

        existingLayer.children[childIndex] = existingChild;
        existingLayers[layerIndex] = existingLayer;
        setLayers(existingLayers);
    }

    const toggleLayerVisibility = (layerIndex) => {
        const existingLayers = [...layers];
        const existingLayer = existingLayers[layerIndex];
        existingLayer.isHidden = !existingLayer?.isHidden;
        existingLayers[layerIndex] = existingLayer;
        setLayers(existingLayers);
    }

    const handleTextDetailChanges = (event, layerIndex, childIndex) => {
        const {name, value, type} = event.target;
        const existingLayers = [...layers];
        const existingLayer = existingLayers[layerIndex];
        let existingChild = existingLayer?.children[childIndex];

        existingChild = handleFieldValueChanges(event, existingChild);

        if (name === 'colorKey' || name === 'colorCodeKey' ||
            name === 'strokeColorKey' || name === 'strokeColorCodeKey' ||
            name === 'shadowColorKey' || name === 'shadowColorCodeKey') {
            processColorDetails(existingChild);
        } else if (name === 'radius' || name === 'rotation' || name === 'letterSpacing' ||
            name === 'startAngle' || name === 'sweepAngle') {
            processTextPathData(existingChild);
        }

        existingLayer.children[childIndex] = existingChild;
        existingLayers[layerIndex] = existingLayer;
        setLayers(existingLayers);
    }

    const handleImageDetailChanges = (event, layerIndex, childIndex) => {
        const {name, value, type} = event.target;
        const existingLayers = [...layers];
        const existingLayer = existingLayers[layerIndex];
        let existingChild = existingLayer?.children[childIndex];
        existingChild = handleFieldValueChanges(event, existingChild);
        const existingChildProps = existingChild?.props || {};

        if (name === 'flip') {
            if (value === FLIP_DIRECTION_KEY.HORIZONTAL) {
                existingChildProps.scaleX = -existingChildProps.scaleX;
            } else if (value === FLIP_DIRECTION_KEY.VERTICAL) {
                existingChildProps.scaleY = -existingChildProps.scaleY;
            }
        } else if (name === 'size') {
            let size = _.parseInt(value);
            if (_.isNaN(size)) {
                size = 100;
            }

            existingChildProps.width = existingChildProps.totalWidth * size / 100;
            existingChildProps.height = existingChildProps.totalHeight * size / 100;
        } else if (name === 'offsetXPercentage') {
            let fillPatternOffsetXPercentage = _.parseInt(value);
            if (_.isNaN(fillPatternOffsetXPercentage)) {
                fillPatternOffsetXPercentage = 0;
            }

            existingChildProps.fillPatternOffsetX = -existingChildProps.width * fillPatternOffsetXPercentage / 100;
            existingChildProps.offsetX = existingChildProps.fillPatternOffsetX;
        } else if (name === 'offsetYPercentage') {
            let fillPatternOffsetYPercentage = _.parseInt(value);
            if (_.isNaN(fillPatternOffsetYPercentage)) {
                fillPatternOffsetYPercentage = 0;
            }

            existingChildProps.fillPatternOffsetY = existingChildProps.width * fillPatternOffsetYPercentage / 100;
            existingChildProps.offsetY = existingChildProps.fillPatternOffsetY;
        } else if (name === 'rotation') {
            let rotationPercentage = _.parseInt(value);
            if (_.isNaN(rotationPercentage)) {
                rotationPercentage = 0;
            }

            existingChildProps.fillPatternRotation = rotationPercentage;
            existingChildProps.rotation = rotationPercentage;
        }

        existingChild.props = existingChildProps;
        existingLayer.children[childIndex] = existingChild;
        existingLayers[layerIndex] = existingLayer;
        setLayers(existingLayers);
    }

    const handleAttributeChanges = (newAttrs, layerIndex, childIndex) => {
        const existingLayers = [...layers];
        const existingLayer = existingLayers[layerIndex];
        existingLayer.children[childIndex] = newAttrs;
        existingLayers[layerIndex] = existingLayer;
        setLayers(existingLayers);
    }

    const prepareStageContainerSize = (currentSettings, isNew = false) => {
        setShowDesigner(false);

        if (isNew || !(currentSettings?.scale && currentSettings?.stageContainer)) {
            const heightRatio = currentSettings.height / 500;
            const widthRatio = currentSettings.width / 500;

            let sizeRatio = heightRatio < widthRatio ? heightRatio : widthRatio;

            const containerWidth = currentSettings.width / sizeRatio;
            const containerHeight = currentSettings.height / sizeRatio;

            currentSettings.stageContainer = {
                width: _.parseInt(containerWidth?.toFixed(0)),
                height: _.parseInt(containerHeight?.toFixed(0))
            };

            setSettings(currentSettings);
        }

        setTimeout(() => {
            setShowDesigner(true);
        }, 1);

    }

    const generateRandomBackgroundColor = (layerIndex, childIndex) => {
        const linearGradientDetail = generateLinearGradientDetail();

        const existingLayers = [...layers];
        const existingLayer = existingLayers[layerIndex];
        const existingChild = existingLayer?.children[childIndex];
        existingChild.settings.background = {
            ...existingChild.settings.background,
            ...linearGradientDetail
        };

        processBackgroundDetails(existingChild?.settings?.background, existingChild.settings);

        existingLayer.children[childIndex] = existingChild;
        existingLayers[layerIndex] = existingLayer;
        setLayers(existingLayers);
    }

    return (
        <>
            {isAddImageMedia &&
                <button
                    className={`${cssClass}`}
                    onClick={() => setShowModal(true)}>
                    Add Image Media
                </button>
            }

            {isEditImageMedia &&
                <button
                    className="button-outline-green px-2 py-1"
                    onClick={() => setShowModal(true)}>
                    Edit Image
                </button>
            }

            {showModal ? (
                <>
                    <div
                        className="justify-center items-center flex overflow-x-hidden overflow-y-auto fixed inset-x-0 top-0 z-50 outline-none focus:outline-none">
                        <div className="relative w-full h-full mx-auto">
                            {/*content*/}
                            <div
                                className="border-0 shadow-lg relative flex flex-col w-full bg-gray-100 outline-none focus:outline-none">
                                {/*header*/}
                                <div className="flex items-center justify-between p-5 border-b border-solid">


                                    <div className="flex justify-start items-center w-2/3">
                                        <h3 className="text-lg font-semibold w-fit mr-10">
                                            {isAddImageMedia &&
                                                <>Add Image Media</>
                                            }

                                            {isEditImageMedia &&
                                                <>Edit Image Media</>
                                            }
                                        </h3>

                                        {imageFields && imageFields.length > 0 && imageMedia &&
                                            <RenderFields
                                                parentForm={imageMedia}
                                                handleDetailChanges={handleImageFieldChanges}
                                                fieldList={imageFields}
                                                enableEdit={true}
                                                fieldClassName={`w-1/2`}/>
                                        }
                                    </div>

                                    <div className="flex items-center justify-end w-1/3">

                                        <div
                                            className="flex items-center justify-end mr-2">
                                            <div className="">
                                                <ShowProgressSpinner dataFetchInProgress={saveInProgress}/>
                                            </div>
                                        </div>

                                        {isCollection &&
                                            <UpdateShopifyCollectionImage
                                                currentStageScale={currentStageScale}
                                                currentStageRef={currentStageRef}
                                                setSaveInProgress={setSaveInProgress}
                                                collection={mediaFor}
                                                imageBase64={imageBase64}/>
                                        }

                                        <button
                                            className="button-green px-3 py-2 mr-2"
                                            onClick={saveImageMediaDetails}>
                                            Save Image Media
                                        </button>

                                        <button
                                            className="button-green px-3 py-2 mr-2"
                                            onClick={handleConfirmAction}>
                                            Confirm Image Media
                                        </button>

                                        <button
                                            className="button-gray-outline px-3 py-2"
                                            onClick={() => setShowModal(false)}>
                                            Cancel
                                        </button>
                                    </div>
                                </div>


                                <div className="flex justify-center w-full image-editor-container p-6">

                                    <div className="w-1/3">
                                        <div
                                            className="h-full content-start justify-start overflow-y-scroll mx-2 p-4 border border-gray-400 bg-gray-200 rounded-lg">

                                            <div className={'flex justify-between items-center'}>
                                                <div className={'text-lg font-semibold'}>Layers</div>
                                                <div>
                                                    <button
                                                        className="button-gray-outline pl-2 pr-3 py-1 flex justify-center items-center font-semibold"
                                                        onClick={addNewLayer}>
                                                        <PlusIcon className="h-6 w-6 mr-1"/>
                                                        New Layer
                                                    </button>
                                                </div>
                                            </div>

                                            <div className={'mt-4'}>
                                                {layers?.map((layer, layerIndex) => (
                                                    <div key={layerIndex}
                                                         className={`border bg-white px-3 py-2 rounded my-2 ${selectedLayer?.key === layer?.key ? 'bg-purple-100 border-purple-400' : 'border-gray-200'}`}>

                                                        <div className={'flex justify-between items-center my-2 mx-2'}>
                                                            <div
                                                                onClick={() => {
                                                                    setSelectedLayer(layer);
                                                                }}
                                                                className={'text-purple-500 cursor-pointer text-lg font-semibold'}>Layer {layerIndex + 1}</div>

                                                            <div className={'flex justify-center items-center'}>
                                                                <button
                                                                    className="button-outline-green px-2 py-1 flex justify-center items-center mr-2"
                                                                    onClick={() => {
                                                                        showOrHideBackgroundColorSettings(layer);
                                                                    }}>
                                                                    <SwatchIcon className="h-5 w-5"/>
                                                                </button>

                                                                {allDesignInfo &&
                                                                    <SelectDesignProductImages
                                                                        allDesignInfo={allDesignInfo}
                                                                        allDesigns={allDesigns}
                                                                        layer={layer}
                                                                        mediaFor={mediaFor}
                                                                        setSelectedLayer={setSelectedLayer}
                                                                        isSingleImageSelection={true}
                                                                        handleSelectedProductImageInLayer={handleSelectedProductImageInLayer}/>
                                                                }

                                                                {/*<button
                                                                    className="button-purple-outline px-2 py-1 flex justify-center items-center mr-2"
                                                                    onClick={() => {
                                                                        addNewImage(layer);
                                                                    }}>
                                                                    <PhotoIcon className="h-5 w-5"/>
                                                                </button>*/}

                                                                <button
                                                                    className="button-sky-outline px-2 py-1 flex justify-center items-center"
                                                                    onClick={() => {
                                                                        addNewText(layer);
                                                                    }}>
                                                                    <DocumentTextIcon className="h-5 w-5"/>
                                                                </button>
                                                            </div>

                                                            <button
                                                                onClick={() => {
                                                                    toggleLayerVisibility(layerIndex);
                                                                }}
                                                                className="button-outline-sky p-2">
                                                                {!layer?.isHidden &&
                                                                    <EyeIcon className="h-6 w-6 text-green-500"/>
                                                                }

                                                                {layer?.isHidden &&
                                                                    <EyeSlashIcon className="h-6 w-6 text-red-500"/>
                                                                }
                                                            </button>

                                                            <DeleteItem
                                                                handleDeleteItem={() => {
                                                                    removeLayer(layer);
                                                                }}
                                                                iconSize={'h-5 w-5'}
                                                                itemIndex={layerIndex}
                                                                showIcon={true}/>
                                                        </div>

                                                        {layer?.children?.length > 0 &&
                                                            <div className={'mt-4'}>
                                                                {layer?.children?.map((child, childIndex) => (

                                                                    <div key={childIndex}
                                                                         className={'border border-gray-200 p-2 m-2 rounded bg-gray-100'}>

                                                                        <div
                                                                            className={'flex justify-between items-center'}>

                                                                            {child?.isImage &&
                                                                                <div onClick={() => {
                                                                                    setSelectedImage(child);
                                                                                    toggleChildEditable(layerIndex, childIndex);
                                                                                }}>
                                                                                    <ProductImageSmall
                                                                                        afterImageSelected={(event) => {
                                                                                        }}
                                                                                        dimension={40}
                                                                                        isSelected={selectedImage?.id === child?.id}
                                                                                        key={`selected-image-${childIndex}`}
                                                                                        imageUrl={child?.url}/>
                                                                                </div>
                                                                            }

                                                                            {child?.isText &&
                                                                                <>
                                                                                    <div
                                                                                        className="text-purple-500 cursor-pointer ml-2 py-2 font-semibold"
                                                                                        onClick={() => {
                                                                                            setSelectedImage(null);
                                                                                            // setShowTextSettings(!showTextSettings);
                                                                                            toggleChildEditable(layerIndex, childIndex);
                                                                                        }}>
                                                                                        {child?.text}
                                                                                    </div>
                                                                                </>
                                                                            }

                                                                            {child?.isBackground &&
                                                                                <>
                                                                                    <div
                                                                                        className="text-purple-500 cursor-pointer ml-2 py-2 font-semibold"
                                                                                        onClick={() => {
                                                                                            setSelectedImage(null);
                                                                                            setShowBackgroundColorSettings(!showBackgroundColorSettings);
                                                                                        }}>
                                                                                        Background Color & Shade
                                                                                    </div>

                                                                                    <button
                                                                                        className="button-sky-outline px-2 py-1 flex justify-center items-center"
                                                                                        onClick={() => {
                                                                                            generateRandomBackgroundColor(layerIndex, childIndex);
                                                                                        }}>
                                                                                        Random Color
                                                                                    </button>
                                                                                </>
                                                                            }

                                                                            {selectedLayer?.key === layer?.key &&
                                                                                <div className={'mt-1 mr-1'}>
                                                                                    <DeleteItem
                                                                                        handleDeleteItem={() => {
                                                                                            removeChild(layerIndex, childIndex);
                                                                                        }}
                                                                                        iconSize={'h-4 w-4'}
                                                                                        itemIndex={childIndex}
                                                                                        showIcon={true}/>
                                                                                </div>
                                                                            }
                                                                        </div>

                                                                        <div>
                                                                            {child?.isBackground && showBackgroundColorSettings &&
                                                                                <>
                                                                                    <div className={''}>
                                                                                        <SelectColorTypeDetails
                                                                                            hideLabel={true}
                                                                                            label={'Background Color & Shade'}
                                                                                            parentForm={child?.settings?.background}
                                                                                            colorAttribute={'background'}
                                                                                            colorKeyAttribute={'backgroundColorKey'}
                                                                                            colorCodeKeyAttribute={'backgroundColorCodeKey'}
                                                                                            settings={child?.settings}
                                                                                            handleColorDetailsChanges={(event) => {
                                                                                                handleSettingsChanges(event, layerIndex, childIndex);
                                                                                            }}
                                                                                            handleClearColorDetailsChanges={(event) => {
                                                                                            }}/>
                                                                                    </div>
                                                                                </>
                                                                            }
                                                                        </div>

                                                                        <div>
                                                                            {child?.isText && child?.isEditable &&
                                                                                <>
                                                                                    <div className="mt-2">

                                                                                        <div
                                                                                            className="field-container w-full">
                                                                                            <div
                                                                                                className="field-label">Text
                                                                                            </div>
                                                                                            <textarea
                                                                                                id="text"
                                                                                                name="text"
                                                                                                value={child?.text}
                                                                                                onChange={(event) => {
                                                                                                    handleTextDetailChanges(event, layerIndex, childIndex);
                                                                                                }}
                                                                                                className="text-area-field h-28"/>
                                                                                        </div>

                                                                                        <EditFontDetails
                                                                                            displayTextItem={child}
                                                                                            handleFontDetailsChanges={(event) => {
                                                                                                handleTextDetailChanges(event, layerIndex, childIndex);
                                                                                            }}/>

                                                                                        <SelectColorTypeDetails
                                                                                            label={'Text Color & Shade'}
                                                                                            parentForm={child}
                                                                                            colorAttribute={'color'}
                                                                                            colorKeyAttribute={'colorKey'}
                                                                                            colorCodeKeyAttribute={'colorCodeKey'}
                                                                                            settings={child?.settings}
                                                                                            handleColorDetailsChanges={(event) => {
                                                                                                handleTextDetailChanges(event, layerIndex, childIndex);
                                                                                            }}
                                                                                            handleClearColorDetailsChanges={() => {
                                                                                            }}/>

                                                                                        <EditStrokeDetails
                                                                                            parentForm={child}
                                                                                            handleStrokeDetailsChanges={(event) => {
                                                                                                handleTextDetailChanges(event, layerIndex, childIndex);
                                                                                            }}/>

                                                                                        <EditShadowDetails
                                                                                            parentForm={child}
                                                                                            handleShadowDetailsChanges={(event) => {
                                                                                                handleTextDetailChanges(event, layerIndex, childIndex);
                                                                                            }}/>

                                                                                        <EditRotationDetails
                                                                                            parentItem={child}
                                                                                            handleRotationDetailsChanges={(event) => {
                                                                                                handleTextDetailChanges(event, layerIndex, childIndex);
                                                                                            }}/>

                                                                                        <EditTextPathDetails
                                                                                            parentItem={child}
                                                                                            handleTextPathDetailsChanges={(event) => {
                                                                                                handleTextDetailChanges(event, layerIndex, childIndex);
                                                                                            }}/>
                                                                                    </div>
                                                                                </>
                                                                            }
                                                                        </div>

                                                                        <div>
                                                                            {child?.isImage && child?.isEditable &&
                                                                                <>
                                                                                    <EditFlipDetails
                                                                                        item={child}
                                                                                        handleFlipDetailsChanges={(event) => {
                                                                                            handleImageDetailChanges(event, layerIndex, childIndex);
                                                                                        }}/>

                                                                                    <div
                                                                                        className="flex flex-wrap justify-start">
                                                                                        <RenderFields
                                                                                            parentForm={child}
                                                                                            handleDetailChanges={(event) => {
                                                                                                handleImageDetailChanges(event, layerIndex, childIndex);
                                                                                            }}
                                                                                            fieldList={_.groupBy(IMAGE_FIELD_LIST, 'group')['dimension']}
                                                                                            enableEdit={true}
                                                                                            fieldClassName={'w-1/2'}/>
                                                                                    </div>
                                                                                </>
                                                                            }
                                                                        </div>

                                                                    </div>

                                                                ))}
                                                            </div>
                                                        }

                                                    </div>
                                                ))}
                                            </div>

                                        </div>

                                    </div>

                                    <div
                                        className="w-2/3 overflow-scroll bg-gray-200 border border-gray-400 rounded-lg p-4">
                                        {imageMedia && settings && settings?.stageContainer && showDesigner &&
                                            <RenderMediaStage
                                                setCurrentStageRef={setCurrentStageRef}
                                                setCurrentStageScale={setCurrentStageScale}
                                                dimensionsMap={dimensionsMap}
                                                handleAttributeChanges={handleAttributeChanges}
                                                selectedImage={selectedImage}
                                                setSelectedImage={setSelectedImage}
                                                imageMedia={imageMedia}
                                                setImageBase64={setImageBase64}
                                                layers={layers}
                                                setSettings={setSettings}
                                                settings={settings}/>
                                        }
                                    </div>

                                </div>

                            </div>
                        </div>
                    </div>
                    <div className="opacity-25 fixed inset-0 z-40 bg-black"></div>
                </>
            ) : null}
        </>
    );
}

export default AddEditImageMedia
